import type { PatientReferralType } from 'app/services/metrics'
import { ColumnDef } from '@tanstack/react-table'
import { compact } from 'lodash'
import { FormEvent, useMemo, useState } from 'react'
import Combo from 'app/components/ui/combo/combo'
import { ContactsByReferralType } from 'app/components/settings/mapping/ContactsByReferralType'
import { InlineEditCell } from 'app/components/table/InlineEditCell'
import { LazyLocationsCell } from 'app/components/table/LazyLocationsCell'
import SimpleTable from 'app/components/table/SimpleTable'
import { Button } from 'app/components/ui/button'
import { PageScroll } from 'app/components/layout/PageScroll'
import { showModal } from 'app/lib/modal'
import { isSMCOrg } from 'lib/isSMCOrg'
import { debug } from 'lib/logging'
import { referralFilterableColumns } from './useReferralMapping'

export type ReferralMappingShadcnComponentProps = {
  canEditMapping: boolean
  filteredRows: PatientReferralType[] | undefined
  isSaving: boolean
  orgId: string
  onSave: (e: FormEvent) => void
  onSearchValueChange: (value: string) => void
}

function TextInput({ property, mapping }: { property: string; mapping: PatientReferralType | undefined }) {
  if (!mapping) return null
  return (
    <InlineEditCell
      value={mapping[property]}
      handleSubmit={(value?: string) => {
        // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
        mapping![property] = value
        mapping!.changed = true
      }}
    />
  )
}

const getDefaultColumns = (orgId: string, canEditMapping: boolean): ColumnDef<PatientReferralType, any>[] =>
  compact([
    {
      id: `Referral Type`,
      accessorFn: (row) => row.referralType,
      header: `Referral Type`,
      meta: {
        defaultVisible: true,
        pinned: true,
        onOpen(row) {
          showModal(<ContactsByReferralType referralType={row.referralType} />, {
            hideTitle: true,
            removePadding: true,
            isFullScreen: true,
            useRadix: true,
          })
        },
      },
      enableSorting: true,
    },
    !isSMCOrg(orgId) && {
      id: `source`,
      accessorFn: (row) => row.source,
      header: `Source`,
      meta: {
        defaultVisible: true,
        ...(canEditMapping && { ControlledComponent: (row) => <TextInput property="source" mapping={row} /> }),
      },
    },
    !isSMCOrg(orgId) && {
      id: `medium`,
      accessorFn: (row) => row.medium,
      header: `Medium`,
      meta: {
        defaultVisible: true,
        ...(canEditMapping && { ControlledComponent: (row) => <TextInput property="medium" mapping={row} /> }),
      },
    },
    !isSMCOrg(orgId) && {
      id: `campaign`,
      accessorFn: (row) => row.campaign,
      header: `Campaign`,
      meta: {
        defaultVisible: true,
        ...(canEditMapping && { ControlledComponent: (row) => <TextInput property="campaign" mapping={row} /> }),
      },
    },
    isSMCOrg(orgId) && {
      id: `Channel`,
      header: `Channel`,
      accessorFn: (row) => row.channel,
      meta: {
        defaultVisible: true,
        tooltipClassName: `overflow-visible`,
        ControlledComponent: (row) => {
          const [channel, setChannel] = useState(
            row?.channel ? { name: row.channel, value: row.channel, id: row.channel } : undefined,
          )
          return (
            <Combo
              inputClassName="!h-full !border-0 !shadow-none !p-2 text-body-sm text-strong"
              placeholder=""
              disabled={!canEditMapping}
              options={[
                { name: `Digital Marketing`, value: `Digital Marketing`, id: `Digital Marketing` },
                { name: `Referral Marketing`, value: `Referral Marketing`, id: `Referral Marketing` },
                { name: `Traditional Marketing`, value: `Traditional Marketing`, id: `Traditional Marketing` },
                { name: `None`, value: null, id: `None` },
              ]}
              value={channel}
              onChange={(option) => {
                setChannel(option)
                row!.channel = option.value
                row!.changed = true
              }}
            />
          )
        },
      },
      enableSorting: false,
    },
    {
      id: `Count`,
      header: `Count`,
      accessorFn: (row) => row.count,
      meta: { defaultVisible: true },
      enableSorting: true,
    },
    {
      accessorFn: (row) => row.locationIds,
      id: `Locations`,
      header: `Locations`,
      meta: {
        defaultVisible: true,
        ControlledComponent: (row) => <LazyLocationsCell locationIds={row?.locationIds ?? []} />,
      },
      enableSorting: false,
    },
    !isSMCOrg(orgId) && {
      id: `force`,
      header: `Force Attribution`,
      cell: function ForceAttribution(props) {
        const [checked, setChecked] = useState(props.row.original.force)
        return (
          <input
            type="checkbox"
            checked={checked}
            disabled={!canEditMapping}
            name="force"
            onChange={(val) => {
              debug(`channel`, `force`, val.target.checked)
              props.row.original.force = val.target.checked
              props.row.original.changed = true
              setChecked(val.target.checked)
            }}
          />
        )
      },
      meta: {
        defaultVisible: true,
        infoTooltip: {
          title: `Force Attribution`,
          message: `If this is checked, then a patient that matches will always use this mapping, otherwise the first in attribution will be used.`,
        },
      },
    },
  ])

export function ReferralMappingShadcnComponent({
  canEditMapping,
  filteredRows,
  isSaving,
  orgId,
  onSave,
  onSearchValueChange,
}: ReferralMappingShadcnComponentProps) {
  const defaultColumns = useMemo(() => getDefaultColumns(orgId, canEditMapping), [orgId, canEditMapping])

  return (
    <PageScroll variant="table" contentClassName="pt-6">
      <form className="flex h-full flex-col overflow-hidden" onSubmit={onSave}>
        <SimpleTable
          className="grow"
          data={filteredRows}
          columns={defaultColumns}
          filterableColumns={referralFilterableColumns}
          hideFooter={true}
          onSearchValueChange={onSearchValueChange}
          edgePadded
        />
        {canEditMapping && (
          <div className="flex grow-0 justify-start gap-x-4 bg-surface py-4">
            <Button type="submit" variant="primary" size="sm" disabled={isSaving}>
              {isSaving ? `Saving…` : `Save`}
            </Button>
          </div>
        )}
      </form>
    </PageScroll>
  )
}
